import { Injectable, computed, signal } from "@angular/core";

import {
  DARK_SCHEME_QUERY,
  DEFAULT_DENSITY,
  DEFAULT_THEME,
  DENSITIES,
  DENSITY_STORAGE_KEY,
  Density,
  ResolvedTheme,
  THEME_CHOICES,
  THEME_STORAGE_KEY,
  ThemeChoice,
  isDensity,
  isThemeChoice
} from "./appearance.tokens";

/**
 * Owns the two per-user appearance axes the token file already ships palettes for — theme and
 * density — and writes them onto `<html>` as `data-theme` / `data-density`, where
 * `themes/_design-tokens.scss` picks them up.
 *
 * Deliberately *not* named `ThemeService`: that name belongs to
 * `core/services/theme-color/theme.service.ts`, which applies the **tenant's** branding. Both are
 * injected into the shell — tenant identity is not a user preference and must not be switchable
 * from a profile menu.
 *
 * The third axis in the prototype's Tweaks panel, `data-brand-preset`, is deliberately unwired.
 * See `themes/_design-tokens.scss` for why.
 */
@Injectable({ providedIn: "root" })
export class AppearanceService {
  private readonly themeChoice = signal<ThemeChoice>(DEFAULT_THEME);
  private readonly densityChoice = signal<Density>(DEFAULT_DENSITY);
  private readonly systemPrefersDark = signal(false);

  readonly themes: readonly ThemeChoice[] = THEME_CHOICES;
  readonly densities: readonly Density[] = DENSITIES;

  readonly theme = this.themeChoice.asReadonly();
  readonly density = this.densityChoice.asReadonly();

  /** What is actually painted: the explicit choice, or the OS setting while the choice is `system`. */
  readonly resolvedTheme = computed<ResolvedTheme>(() => {
    const choice = this.themeChoice();
    if (choice === "system") {
      return this.systemPrefersDark() ? "dark" : "light";
    }
    return choice;
  });

  readonly isDark = computed(() => this.resolvedTheme() === "dark");

  /**
   * Resolves the startup appearance and paints it before the first route renders.
   *
   * Runs as an app initializer rather than from the shell: the auth screens and the public
   * funnel render outside the shell, and a preference that only applied once you were signed in
   * would flash the wrong theme on every cold load.
   */
  init(): void {
    this.watchSystemScheme();
    const storedTheme = this.read(THEME_STORAGE_KEY);
    if (isThemeChoice(storedTheme)) {
      this.themeChoice.set(storedTheme);
    }
    const storedDensity = this.read(DENSITY_STORAGE_KEY);
    if (isDensity(storedDensity)) {
      this.densityChoice.set(storedDensity);
    }
    this.apply();
  }

  /**
   * `system` is a real, persisted choice, not the absence of one — it re-subscribes to the OS
   * after the user has been explicit, which is the only way back once they have chosen.
   */
  setTheme(choice: ThemeChoice): void {
    if (!isThemeChoice(choice)) {
      return;
    }
    this.themeChoice.set(choice);
    this.persist(THEME_STORAGE_KEY, choice);
    this.apply();
  }

  setDensity(density: Density): void {
    if (!isDensity(density)) {
      return;
    }
    this.densityChoice.set(density);
    this.persist(DENSITY_STORAGE_KEY, density);
    this.apply();
  }

  private apply(): void {
    const root = document.documentElement;
    root.setAttribute("data-theme", this.resolvedTheme());
    root.setAttribute("data-density", this.densityChoice());
  }

  /**
   * Keeps following the OS for as long as the choice is `system`. The listener stays attached
   * whatever the choice is, so a user who switches back to `system` is already in sync; the
   * `resolvedTheme` computed is what decides whether the OS signal is consulted.
   */
  private watchSystemScheme(): void {
    if (typeof window.matchMedia !== "function") {
      return;
    }
    const query = window.matchMedia(DARK_SCHEME_QUERY);
    this.systemPrefersDark.set(query.matches);

    const onChange = (event: MediaQueryListEvent): void => {
      this.systemPrefersDark.set(event.matches);
      this.apply();
    };

    // Safari only gained addEventListener on MediaQueryList in 14; addListener is the fallback
    // and is still the only thing some embedded webviews implement.
    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", onChange);
    } else if (typeof query.addListener === "function") {
      query.addListener(onChange);
    }
  }

  // localStorage throws in private-mode Safari and when storage is disabled; an appearance
  // preference is never worth breaking startup over.
  private persist(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // ignore
    }
  }

  private read(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }
}
